// Where each thought sits in the endless Sky of Thoughts. Kept apart from the
// Sky itself (InfiniteSky), so the page can work out the layout while the Sky's
// own code loads in the background.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Thought } from '../types';
import { CHUNK, GAP, SkyPlacer, chunkIndex, chunkKey, emojiRadius, type Pt, type SkyGeometry } from '../lib/skyLayout';

export type SkyApi = {
  /** Camera position (sky px, top-left of the screen). */
  cam: () => Pt;
  scale: () => number;
  /** A sky spot to a screen point (relative to the Sky's own box). */
  toScreen: (p: Pt, cam?: Pt) => Pt;
  /** Move the camera so `p` is in view; returns where the camera will be. */
  reveal: (p: Pt, instant?: boolean) => Pt;
  home: () => void;
  atHome: () => boolean;
  /** The list of thoughts (the button for it sits in the page's top bar). */
  openList: () => void;
  closeList: () => void;
};

export type SkyLayout = {
  spots: Map<string, Pt>;
  scale: number;
  geo: SkyGeometry;
  drift: Map<string, number>;
  homeIds: Set<string>;
  capacity: number;
  reserve: (id: string) => Pt | null;
};

// Measure the Sky and every control marked `data-sky-avoid`, then place
// thoughts. Only a real size change (not the phone's address bar showing or
// hiding) lays the Sky out again.
export function useSkyLayout(thoughts: Thought[], skyRef: React.RefObject<HTMLDivElement | null>, active: boolean): SkyLayout | null {
  const [geo, setGeo] = useState<SkyGeometry | null>(null);

  useEffect(() => {
    if (!active) return;
    const measure = (): SkyGeometry | null => {
      const el = skyRef.current;
      if (!el) return null;
      const box = el.getBoundingClientRect();
      const avoid = Array.from(document.querySelectorAll('[data-sky-avoid]'))
        .map(n => n.getBoundingClientRect())
        .filter(r => r.width > 0 && r.height > 0)
        .map(r => ({ x: r.left - box.left, y: r.top - box.top, w: r.width, h: r.height }));
      const w = box.width, h = Math.min(box.height, window.innerHeight);
      return { w, h, scale: w < 640 ? 0.85 : 1, avoid };
    };
    // Wait a beat so the header and chips have their final size.
    const timer0 = setTimeout(() => setGeo(measure()), 60);
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const next = measure();
        setGeo(prev => {
          if (!prev || !next) return next;
          const big = Math.abs(next.w - prev.w) > 80 || Math.abs(next.h - prev.h) > 160 || (next.w >= 640) !== (prev.w >= 640);
          return big ? next : prev;
        });
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(timer0); clearTimeout(timer); window.removeEventListener('resize', onResize); };
  }, [active, skyRef]);

  const placer = useMemo(() => (geo ? new SkyPlacer(geo) : null), [geo]);

  const spots = useMemo(() => (placer ? placer.sync(thoughts.map(t => t.id)) : null), [placer, thoughts]);

  // How far each thought may float: half the free room to its nearest
  // neighbour, so two floating emojis can never touch.
  const drift = useMemo(() => {
    const out = new Map<string, number>();
    if (!spots) return out;
    const idx = chunkIndex(spots);
    const byId = new Map(thoughts.map(t => [t.id, t]));
    const rOf = (id: string) => { const t = byId.get(id); return t ? emojiRadius(t.size, t.replies?.length) : 24; };
    for (const [id, p] of spots) {
      const cx = Math.floor(p.x / CHUNK), cy = Math.floor(p.y / CHUNK), r = rOf(id);
      let gap = GAP;
      for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
        for (const o of idx.get(chunkKey(cx + i, cy + j)) ?? []) {
          if (o === id) continue;
          const q = spots.get(o)!;
          const d = Math.hypot(q.x - p.x, q.y - p.y);
          if (d < GAP * 2.5) gap = Math.min(gap, d - r - rOf(o));
        }
      }
      out.set(id, Math.max(2, Math.min(10, (gap - 6) / 2)));
    }
    return out;
  }, [spots, thoughts]);

  const homeIds = useMemo(() => new Set(spots && placer ? [...spots.keys()].filter(id => placer.isHome(id)) : []), [spots, placer]);
  const reserve = useCallback((id: string) => placer?.reserve(id) ?? null, [placer]);

  if (!geo || !placer || !spots) return null;
  return { spots, scale: geo.scale, geo, drift, homeIds, capacity: placer.capacity, reserve };
}
